import { classifyFailure, createStaffSessionApi } from "@lafayette/api-client";
import type { StaffSession } from "@lafayette/schemas";

export type StaffSessionOutcome =
  | Readonly<{ status: "ok"; session: StaffSession }>
  // The API explicitly refused the session (401/403) or answered with an unusable payload. `httpStatus` is the refusal when there was one
  // (403 is a session that is valid but not staff: a customer; 401 is a session the API does not know or that expired).
  | Readonly<{ status: "denied"; httpStatus?: number }>
  // The API could not answer (5xx or network failure); the session may still be valid.
  | Readonly<{ status: "unavailable" }>;

export async function resolveTrustedStaffSession(
  apiUrl: URL,
  accessToken: string,
  fetcher: typeof fetch = fetch,
): Promise<StaffSessionOutcome> {
  // The call is the typed client of the API: one try, no retry, the bearer token of this session. `/staff/session` is an absolute path, so
  // the origin of the API is the base (a path in `apiUrl` was never part of it).
  const client = createStaffSessionApi({
    baseUrl: apiUrl.origin,
    getAccessToken: () => accessToken,
    fetch: fetcher,
  });
  try {
    return { status: "ok", session: await client.get() };
  } catch (error) {
    const failure = classifyFailure(error);
    // The API could not answer (no answer, or a 5xx): the session may still be valid.
    if (failure.kind === "network" || (failure.status !== null && failure.status >= 500)) {
      return { status: "unavailable" };
    }
    // The API refused the session (401, 403 for a customer), or its answer is not the typed grant: fail closed.
    return failure.status === null
      ? { status: "denied" }
      : { status: "denied", httpStatus: failure.status };
  }
}

export async function fetchTrustedStaffSession(
  apiUrl: URL,
  accessToken: string,
  fetcher: typeof fetch = fetch,
): Promise<StaffSession | null> {
  const outcome = await resolveTrustedStaffSession(apiUrl, accessToken, fetcher);
  return outcome.status === "ok" ? outcome.session : null;
}
